import { appRoot } from '@libs/focus-frontend-services/src/UrlGeneratorService/UrlGeneratorService';
import { camelizeKeys } from 'humps';
import { normalize } from 'normalizr';
import { stringify } from 'qs';

/**
 * Makes the actual AJAX request
 * @param {String} endpoint
 * @param {Object} options
 * @param {Object} schema
 * @returns {Promise<R>|Promise.<TResult>|Promise<R2|R1>}
 */
const call = async (endpoint, options = {}, schema) => {
  let params = '';
  if (options.params) {
    params = endpoint.includes('?') ? '&' : '?';
    params += stringify(options.params, {
      arrayFormat: 'brackets',
    });
  }

  const response = await fetch(`${appRoot()}${endpoint}${params}`, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: options.data ? JSON.stringify(options.data) : undefined,
  });

  if (!response.ok) {
    throw { response }; // eslint-disable-line no-throw-literal
  }

  const json = await response.json();
  const camelizedJson = camelizeKeys(json.results || json, (key, convert) => {
    return /_/.test(key) ? convert(key) : key;
  });

  if (Object.prototype.hasOwnProperty.call(camelizedJson, 'items')) {
    return schema ? normalize(camelizedJson, { items: schema }) : camelizedJson;
  }

  return schema ? normalize(camelizedJson, { data: schema }) : camelizedJson;
};

/**
 * Makes an authorized AJAX request
 * @param {String} endpoint
 * @param {Object} options - needs to have at least a valid `accessToken`
 * @param {Object} schema
 */
export const authorizedCall = (endpoint, options = { accessToken: null, headers: {} }, schema) => {
  if (!options.accessToken) {
    throw new Error('A valid accessToken needs to be passed');
  }
  const headers = { ...options.headers, Authorization: `Bearer ${options.accessToken}` };

  return call(endpoint, { ...options, method: 'POST', headers }, schema);
};

const dispatch = ({ commandName, payload, accessToken }) => {
  return fetch(`${appRoot()}/api/dispatcher`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      commandName,
      payload,
    }),
  });
};

const findZonesForZipcode = (options) => authorizedCall(`/api/addresses/findZonesForZipcode`, options);
const findRoutingProperties = (options) => authorizedCall(`/api/addresses/findRoutingForZipcodeAndCountry`, options);
const findBusinessTypes = (options) => authorizedCall(`/api/business-types`, options);
const findDutchAddressByZipcodeAndNumber = (options) =>
  authorizedCall(`/api/addresses.findDutchAddressByZipcodeAndNumber`, options);
const fetchTaskOverviewItems = (options) => {
  const { selectedProjectId, selectedEmployeeId, selectedDay } = options;
  const projectId = selectedProjectId ? '&projectId=' + selectedProjectId : '';

  return authorizedCall(
    `/api/planning/task-overview?employeeId=${selectedEmployeeId}${projectId}&selectedDay=${selectedDay}`,
    options,
  );
};
const fetchProvinces = (options) => authorizedCall('/api/addresses/provinces', options);

const Teamleader = {
  dispatch,
  findZonesForZipcode,
  findRoutingProperties,
  findDutchAddressByZipcodeAndNumber,
  findBusinessTypes,
  fetchTaskOverviewItems,
  fetchProvinces,
};

export default Teamleader;
